import React, { useCallback, useEffect, useState } from "react";
import styles from './InstructorForm.module.scss';
import PropTypes from "prop-types";
import ImageUploader from "../imageUploader/ImageUploader";
import Dropdown from "../dropdown/Dropdown";
import TextField from "../textField/TextField";
import MultiSelectDropdown from "../multiSelectDropdown/MultiSelectDropdown";
import Button from "../button/Button";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber, getInitials } from "../../utils/stringUtils";

const InstructorForm = ({
  formData,
  titleList = [],
  specialityList = [],
  healthSystemList = [],
  clinicalSiteList = [],
  roleList = [],
  isTemporaryInstructor = false,
  onDataChange,
  onSubmit
}) => {
  const [profilePicture, setProfilePicture] = useState({
    initials: '',
    image: null
  });
  const [imageFile, setImageFile] = useState(null);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [roleIdError, setRoleIdError] = useState('');
  const [isValidated, setIsValidated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setProfilePicture({
      initials: getInitials(formData?.firstName, formData?.lastName),
      image: formData?.profilePicture || null
    });
  }, [formData]);

  const validateForm = useCallback(() => {
    const validateFirstName = () => {
      if (!formData.firstName) {
        return 'First Name is required';
      }
      if (!/^[A-Za-z]{2,}/.test(formData.firstName)) {
        return 'First Name must contain only letters and be at least 2 characters long.';
      }
      return '';
    };

    const validateLastName = () => {
      if (!formData.lastName) {
        return 'Last Name is required';
      }
      if (!/^[A-Za-z]{2,}/.test(formData.lastName)) {
        return 'Last Name must contain only letters and be at least 2 characters long.';
      }
      return '';
    };

    const validateEmail = () => {
      if (!formData.email) {
        return 'Email is required';
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        return 'Invalid email format';
      }
      return '';
    };

    const validatePhone = () => {
      const cleanedValue = formData.phone.replace(/\D/g, '');
      if (!cleanedValue) {
        return 'Phone number is required';
      }
      if (!/^\d{10}$/.test(cleanedValue)) {
        return 'Invalid phone number format. Must be 10 digits.';
      }
      return '';
    };

    const validateRoleId = () => {
      if (!formData.roleId) {
        return 'Role is required';
      }
      return '';
    };

    setIsValidated(true);
    const firstNameValidationResult = validateFirstName();
    const lastNameValidationResult = validateLastName();
    const emailValidationResult = validateEmail();
    const phoneValidationResult = validatePhone();
    const roleIdValidationResult = validateRoleId();

    setFirstNameError(firstNameValidationResult);
    setLastNameError(lastNameValidationResult);
    setEmailError(emailValidationResult);
    setPhoneError(phoneValidationResult);
    setRoleIdError(roleIdValidationResult);

    const errors = [
      { field: 'firstName', message: firstNameValidationResult },
      { field: 'lastName', message: lastNameValidationResult },
      { field: 'email', message: emailValidationResult },
      { field: 'phone', message: phoneValidationResult },
      { field: 'roleId', message: roleIdValidationResult }
    ];

    const firstError = errors.find(error => error.message !== '');
    return firstError ? firstError.field : null; // Return the field name of the first error
  }, [formData]);

  const handleSubmit = () => {
    const firstErrorField = validateForm();
    if (firstErrorField) {
      scrollToFirstError(firstErrorField);
      return false;
    }

    onSubmit(imageFile);
  };

  const scrollToFirstError = (field) => {
    const headerHeight = 72; // Your fixed header height
    const element = document.querySelector(`[data-error-field="${field}"]`);
    if (element) {
      const elementTop = element.offsetTop - headerHeight; // Calculate adjusted position

      window.scrollTo({
        top: elementTop,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (isValidated) {
      validateForm();
    }
  }, [formData, isValidated, validateForm]);

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <h2>Personal Information</h2>
      <ImageUploader
        {...profilePicture}
        onChange={(value) => {
          setImageFile(value);
        }}
      />
      <div className={styles.row}>
        <div className={styles.column20} data-error-field="title">
          <Dropdown
            label="Title"
            options={titleList}
            value={formData.title}
            onChange={(selected) => {
              onDataChange({
                title: selected
              });
            }}
          />
        </div>
        <div className={styles.column40} data-error-field="firstName">
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(value) => {
              onDataChange({
                firstName: value
              });
            }}
            error={firstNameError}
          />
        </div>
        <div className={styles.column40} data-error-field="lastName">
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(value) => {
              onDataChange({
                lastName: value
              });
            }}
            error={lastNameError}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="email">
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(value) => {
              onDataChange({
                email: value
              });
            }}
            error={emailError}
          />
        </div>
        <div className={styles.column50} data-error-field="phone">
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(value) => {
              onDataChange({
                phone: formatPhoneNumber(value)
              });
            }}
            error={phoneError}
          />
        </div>
      </div>
      <h2>Professional Information</h2>
      <div className={styles.row}>
        <div className={styles.column50}>
          <Dropdown
            label="Specialty"
            options={specialityList}
            value={formData.specialityId}
            onChange={(selected) => {
              onDataChange({
                specialityId: selected
              });
            }}
          />
        </div>
        <div className={styles.column50}>
          <TextField
            label="Qualification"
            value={formData.qualification}
            onChange={(value) => {
              onDataChange({
                qualification: value
              });
            }}
          />
        </div>
      </div>
      <h2>Employment Details</h2>
      <div className={styles.row}>
        <div className={styles.column50}>
          <MultiSelectDropdown
            label="Health Systems"
            options={healthSystemList}
            selected={formData.healthSystemIds}
            onChange={(selected) => {
              onDataChange({
                healthSystemIds: selected
              });
            }}
          />
        </div>
        <div className={styles.column50}>
          <MultiSelectDropdown
            label="Clinical Sites"
            options={clinicalSiteList}
            selected={formData.clinicalSiteIds}
            onChange={(selected) => {
              onDataChange({
                clinicalSiteIds: selected
              });
            }}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="roleId">
          <Dropdown
            label="Designation"
            options={roleList}
            value={formData.roleId}
            onChange={(selected) => {
              onDataChange({
                roleId: selected
              });
            }}
            error={roleIdError}
            disabled={true}
          />
        </div>
        <div className={styles.column50}>
          <Dropdown
            label="Instructor Status"
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]}
            value={formData.isActive ? 'Active' : 'Inactive'}
            onChange={(value) => onDataChange({ isActive: value === 'Active' })}
          />
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          label="Cancel"
          variant="white"
          size="medium"
          onClick={() => navigate('/instructors')}
        />
        <Button
          label={`${isTemporaryInstructor ? 'Approve' : 'Save'} Instructor`}
          variant="primary"
          size="medium"
          type="submit"
        />
      </div>
    </form>
  );
};

InstructorForm.propTypes = {
  formData: PropTypes.object,
  titleList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  specialityList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  healthSystemList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  clinicalSiteList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  roleList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  isTemporaryInstructor: PropTypes.bool,
  onDataChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default InstructorForm;